import React, { Component } from "react";

import Page from "EmployeeApplication/Page";
import { basicInformationURL } from "EmployeeApplication/urls";
import ApplicationLayout from "EmployerApplication/Components/ApplicationLayout";
import { connect } from "react-redux";
import companyToken, { companyTokenString } from "Utils/companyToken";
import fetchWithCSRFToken from "Utils/fetchWithCSRFToken";
import { formatNameLF } from "Utils/formatName";
import { baseUrl as basename } from "ZeroRuby/urls";

import { getEmployeeForms, getAnyRenewalEmployeePolicyNotActivated } from "../Selectors";
import { Table, TR, TD, TH } from "Cobalt/TableV2";
import Button from "Common/Button";
import { CardHeader, CardSection } from "Common/Card";
import Icon from "Common/Icon";
import NavigationControls from "Common/NavigationControls";

import type { EmployerRenewalEmployeeForm } from "Utils/types";

import makeBem from "Utils/makeBem";

const bem = makeBem("EmployeeTable");

type EmployeeStatusPageDumbProps = {
  employeeForms: Array<EmployerRenewalEmployeeForm>;
  anyEmployeePolicyNotActivated: boolean;
};

export const EmployeeStatusPageDumb = ({ employeeForms, anyEmployeePolicyNotActivated }: EmployeeStatusPageDumbProps) => (
  <Page title="Employee Open Enrollment">
    <CardHeader>Employee Open Enrollment Status</CardHeader>
    <CardSection>
      <EmployeeRenewalsTable employeeForms={employeeForms} anyEmployeePolicyNotActivated={anyEmployeePolicyNotActivated} />
    </CardSection>
    <NavigationControls noBack saveLink={`${companyTokenString()}/employer-dashboard`} saveButtonText="Back to Dashboard" />
  </Page>
);

type EmployeeRenewalsTableProps = {
  employeeForms: Array<EmployerRenewalEmployeeForm>;
  anyEmployeePolicyNotActivated: boolean;
};

export const EmployeeRenewalsTable = ({ employeeForms, anyEmployeePolicyNotActivated }: EmployeeRenewalsTableProps) => {
  const employeeRows = employeeForms
    .filter(
      (employeeForm: EmployerRenewalEmployeeForm) =>
        ["Declined", "Done", "In Progress", "Not Started", "Incomplete Application", "Ineligible"].indexOf(employeeForm.renewalStatus) >= 0,
    )
    .filter((employeeForm: EmployerRenewalEmployeeForm) => !employeeForm.terminatedAt)
    .map((employee, _index) => <EmployeeRenewalStatus employee={employee} key={employee.hashID} />);

  return (
    <Table>
      <thead>
        <TR>
          <TH>Name</TH>
          <TH>Email</TH>
          {!anyEmployeePolicyNotActivated ? <TH>Status</TH> : null}
          <TH>Email Reminder</TH>
          {!anyEmployeePolicyNotActivated ? <TH>Complete Renewal</TH> : null}
        </TR>
      </thead>
      <tbody>
        {employeeRows.length > 0 ? (
          employeeRows
        ) : (
          <TR>
            <TD colSpan={!anyEmployeePolicyNotActivated ? 4 : 2}>No Renewal Applications started yet.</TD>
          </TR>
        )}
      </tbody>
    </Table>
  );
};

export class EmployeeRenewalStatus extends Component {
  constructor(props) {
    super(props);
    this.state = {
      remindersSent: {},
    };
  }

  handleSendReminder(hashId) {
    const remindersSent = { ...this.state.remindersSent, [hashId]: true };
    this.setState({ remindersSent });
    const url = `/employer-application/ajax/send-employee-application-link?employee_id=${hashId}`;
    const queryString = companyToken() ? `&company_id_token=${companyToken()}` : "";
    return fetchWithCSRFToken(`${url}${queryString}`, {
      method: "POST",
      processData: false,
    });
  }

  render() {
    const { employee } = this.props;

    const emailElement = employee.email ? employee.email : "No email address in system";
    let statusElement = null;
    let renewalLinkText = null;
    switch (employee.renewalStatus) {
      case "Declined":
        statusElement = (
          <div>
            <Icon className={bem("successIcon")} icon="check-circle" flavor="solid" /> Declined{" "}
          </div>
        );

        renewalLinkText = "Edit Renewal";
        break;
      case "Done":
        statusElement = (
          <div>
            <Icon className={bem("successIcon")} icon="check-circle" flavor="solid" /> Done{" "}
          </div>
        );

        renewalLinkText = "Edit Renewal";
        break;
      case "Not Started":
        statusElement = <div>Not Started</div>;
        renewalLinkText = "Start Renewal";
        break;
      case "In Progress":
        statusElement = <div>In Progress</div>;
        renewalLinkText = "Finish Renewal";
        break;
      case "Incomplete Application":
        statusElement = <div>Current Enrollment Unfinished</div>;
        renewalLinkText = "Finish Application";
        break;
      case "Ineligible":
        statusElement = <div>Ineligible</div>;
        renewalLinkText = "";
        break;
    }

    let employeeAdminLink = null;
    let linkElement = null;

    if (employee.renewalStatus != "Incomplete Application") {
      employeeAdminLink = `${basicInformationURL(true, true, employee.hashID)}`;

      linkElement = <a href={`${basename}${employeeAdminLink}`}>{renewalLinkText}</a>;
    }

    return (
      <TR key={employee.id}>
        <TD>{formatNameLF(employee)}</TD>
        <TD>{emailElement}</TD>
        <TD>{statusElement}</TD>
        <TD>
          {employee.email ? (
            this.state.remindersSent[employee.hashID] ? (
              "Sent!"
            ) : (
              <Button id={`sendEmail_${employee.hashID}`} color="link" p={0} onClick={() => this.handleSendReminder(employee.hashID)}>
                <Icon icon="envelope" mr={3} />
                Send
              </Button>
            )
          ) : (
            "\u00a0"
          )}
        </TD>
        <TD>{employee.renewalStatus !== "Ineligible" && linkElement}</TD>
      </TR>
    );
  }
}

const withInitialValues = connect((state) => {
  const employeeForms = getEmployeeForms(state);
  const anyEmployeePolicyNotActivated = getAnyRenewalEmployeePolicyNotActivated(state);

  return {
    employeeForms,
    anyEmployeePolicyNotActivated,
  };
});

const EmployeeStatusPage = withInitialValues(EmployeeStatusPageDumb);

export default EmployeeStatusPage;
